import { log } from '@/lib/logger'
import { Mensaje } from '@/components/brain/Mensaje'
import { ChatInput } from '@/components/brain/ChatInput'
import { ListaDeConversaciones } from '@/components/brain/ListaDeConversaciones'
import {
  listaDeAgentes,
  olvidarConversacion,
  planProgress,
  type AgenteDelBrain,
} from '@/services/brainApi'
import { isThinking, useBrainConversationsQuery, useBrainModelsQuery } from '@/queries/brain'
import { useBrainConversation } from '@/hooks/use-brain-conversation'
import { useFilePicker } from '@/hooks/use-file-picker'
import { ReadingFile } from '@/components/brain/ReadingFile'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Brain, PanelLeftOpen, Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { cn } from '@/lib/utils'
import { useIsMobile } from '@/hooks/use-mobile'
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet'


export function BrainPage() {
  const { t } = useTranslation()
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const { lang } = useParams()
  const esMovil = useIsMobile()
  // En movil arranca cerrado: el cajon tapa la conversacion entera.
  const [lateralAbierto, setLateralAbierto] = useState(() => !esMovil)
  // Las conversaciones se guardan en el servidor: el runtime ya ve cada turno
  // entero, asi que no dependen de que la pestaña siga abierta. **No estan
  // separadas por usuario**, y es consciente: hoy toda peticion entra con el
  // mismo token y la plataforma no sabe quien pregunta. Se separan con el
  // multi-tenant.
  const conversaciones = useBrainConversationsQuery()
  const guardadas = conversaciones.data ?? []
  const { refetch } = conversaciones
  const refrescarConversaciones = useCallback(() => {
    void refetch()
  }, [refetch])
  // A quien se le habla. `null` es el Brain, que no sale en la lista: no es uno
  // de los agentes, es el que opera la plataforma entera.
  const [agentes, setAgentes] = useState<AgenteDelBrain[]>([])
  const [agenteActivo, setAgenteActivo] = useState<AgenteDelBrain | null>(null)
  const cajaRef = useRef<HTMLTextAreaElement>(null)
  // El idioma de la aplicación decide el de la respuesta y el del pensamiento,
  // igual que en el panel (`LocaleLayout`). Sin él, esta pantalla contestaba en
  // el idioma que adivinara el modelo.
  const donde = useMemo(() => ({ pagina: 'brain', ...(lang ? { idioma: lang } : {}) }), [lang])

  // **La conversación es la misma que la del panel** (2.7). Lo que esta
  // pantalla tiene de más -la lista de guardadas, el selector de agente y el
  // lateral- se queda aquí; el envío, la confirmación y los mensajes viven en
  // un solo sitio para que no haya dos que arreglar.
  const {
    messages,
    inputValue,
    setInputValue,
    isLoading,
    nombresHerramientas,
    hiloActivo,
    handleSendMessage,
    detener,
    confirmar,
    cancelar,
    responder,
    elegirArchivo,
    dictar,
    dictando,
    transcribiendo,
    microfono,
    modelo,
    elegirModelo,
    subirArchivo,
    readingFile,
    resolveProposal,
    resolvingProposal,
    abrirHilo,
    nuevoHilo,
  } = useBrainConversation({
    agenteId: agenteActivo?.id,
    onRespuesta: refrescarConversaciones,
    donde,
  })


  // **Su propio selector de archivos** (6.2). El del armazón sube a la
  // conversación del panel y lo abre encima; esta pantalla tiene la suya.
  const { open: adjuntar, picker } = useFilePicker(
    (archivo) => void subirArchivo(archivo),
    t('knowledge.upload_document'),
  )

  const modelos = useBrainModelsQuery().data ?? []

  // Quick actions

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  useEffect(() => {
    listaDeAgentes()
      .then((todos) => setAgentes(todos.filter((a) => a.name !== 'brain')))
      .catch(() => setAgentes([]))
  }, [])

  // Borrar es definitivo: el servidor no guarda una papelera. Si se borra la
  // que se esta leyendo, se abre una nueva en vez de dejar en pantalla los
  // mensajes de algo que ya no existe.
  const borrarConversacion = async (hilo: string) => {
    try {
      await olvidarConversacion(hilo)
    } catch (error) {
      log.error('No se pudo borrar la conversacion', error)
      return
    }
    if (hilo === hiloActivo) nuevoHilo()
    refrescarConversaciones()
  }

  const panelDeConversaciones = (
    <ListaDeConversaciones
      conversaciones={guardadas.map((c) => ({
        id: c.thread_id,
        titulo: c.title,
        updatedAt: c.updated_at,
        thinking: isThinking(c),
        step: planProgress(c.active_run?.plan ?? undefined) ?? undefined,
      }))}
      activa={hiloActivo}
      onElegir={abrirHilo}
      onNueva={nuevoHilo}
      onBorrar={borrarConversacion}
      onOcultar={() => setLateralAbierto(false)}
    />
  )

  // The same box in the empty welcome and under the conversation: written
  // twice, the welcome one was left without the microphone (2026-09-28).
  const caja = {
    agents: agentes,
    inputRef: cajaRef,
    inputValue,
    setInputValue,
    isLoading,
    onSend: handleSendMessage,
    onStop: detener,
    onAttach: adjuntar,
    models: modelos,
    chosenModel: modelo,
    onModelSelect: elegirModelo,
    onToggleRecording: dictar,
    isRecording: dictando,
    isTranscribing: transcribiendo,
    recordingStream: microfono,
    placeholder: t('brain.input_placeholder'),
    addMessagePlaceholder: t('brain.input_placeholder'),
    activeAgent: agenteActivo,
    onAgentSelect: setAgenteActivo,
  }

  return (
    // Pantalla completa, como en el prototipo: el Brain no es una seccion mas
    // dentro del marco de la aplicacion, es una conversacion que ocupa todo.
    // Por eso la barra superior lleva su propio «volver».
    //
    // `z-40` y no `z-[100]`: el 100 no defendia de nada -en todo el marco de la
    // aplicacion no hay un solo elemento fijo con z-index- y dejaba el cajon
    // del lateral, que se dibuja en el `body`, por debajo de esta pantalla. 40
    // sigue creando su contexto de apilamiento y deja pasar al cajon, que va
    // en 50.
    //
    // No es un modal: es la pantalla completa de una ruta, sin nada detras a
    // lo que volver. La directiva va en UNA linea -partida en dos, el
    // `next-line` cae sobre el comentario y no sobre el codigo-.
    // eslint-disable-next-line no-restricted-syntax -- pantalla completa de una ruta, no un modal
    <div className="fixed inset-0 z-40 flex bg-background">
      {picker}
      {/* El lateral. En escritorio es una columna que se pliega; en movil era
          un cajon escrito a mano -velo `bg-black/50`, y nada mas: ni Escape, ni
          foco atrapado, ni scroll bloqueado, ni foco devuelto-. Ahora es
          `Sheet`, que es donde esas cuatro cosas se construyen una vez.
          Y ademas empezaba ABIERTO en movil, tapando la conversacion. */}
      {esMovil ? (
        <Sheet open={lateralAbierto} onOpenChange={setLateralAbierto}>
          <SheetContent side="left" sinCerrar className="w-64 bg-muted/20">
            <SheetTitle className="sr-only">{t('brain.conversations')}</SheetTitle>
            {panelDeConversaciones}
          </SheetContent>
        </Sheet>
      ) : (
        <div
          className={cn(
            'flex-shrink-0 overflow-hidden border-r border-border bg-muted/20 transition-all duration-200',
            lateralAbierto ? 'w-64' : 'w-0',
          )}
        >
          {panelDeConversaciones}
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex h-12 flex-shrink-0 items-center gap-1 border-b border-border px-3 md:px-6">
          {!lateralAbierto && (
            <Button
              variant="ghost"
              size="icon-xs"
              onClick={() => setLateralAbierto(true)}
              className="text-muted-foreground hover:text-foreground"
              title={t('brain.show_sidebar')}
            >
              <PanelLeftOpen className="h-4 w-4" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="xs"
            onClick={() => navigate(`/${lang ?? 'es'}/conversations`)}
            className="gap-2 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('brain.back_to_dashboard')}
          </Button>
          <div className="ml-auto flex items-center gap-2">
            <div className="flex h-6 w-6 items-center justify-center rounded-md bg-primary">
              <Brain className="h-3.5 w-3.5 text-primary-foreground" />
            </div>
            <span className="text-sm font-medium">{t('brain.title')}</span>
          </div>
        </div>

        {messages.length === 0 ? (
          // Bienvenida: la caja va **en el centro**, bajo el saludo, como en el
          // prototipo. Anclada abajo con la pantalla vacia queda un hueco
          // enorme en medio y la conversacion parece haber empezado ya.
          //
          // **Aqui habia cuatro sugerencias y se retiraron el 2026-08-25.**
          // Decian «Reactivar agentes inactivos», «Leads mas calientes hoy»,
          // «Resumen diario de propiedades» y «Optimizar gestion de agentes», y
          // ninguna de las cuatro se puede hacer hoy: no hay capacidad detras.
          // Una sugerencia es una promesa, y la primera que falla enseña a no
          // fiarse de la pantalla. Vuelven cuando exista que ejecutar.
          <div className="flex flex-1 flex-col items-center justify-center px-4 md:px-6">
            <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-lg bg-primary/20">
              <Sparkles className="h-8 w-8 text-primary" />
            </div>
            <h2 className="mb-2 text-2xl font-semibold">{t('brain.welcome_title')}</h2>
            <p className="mb-8 max-w-md text-center text-muted-foreground">
              {t('brain.welcome_desc')}
            </p>
            <div className="w-full max-w-2xl">
              <ChatInput
                {...caja}
                large
              />
            </div>
          </div>
        ) : (
          <>
            <ScrollArea className="h-full flex-1 [&>div>div]:!block">
              <div className="w-full px-4 pb-6 pt-6 md:px-6">
                <div className="mx-auto w-full min-w-0 max-w-3xl">
                  {messages.map((message, i) => (
                    <div
                      key={message.id}
                      className={i === 0 ? 'mt-0' : message.role === 'assistant' ? 'mt-3' : 'mt-6'}
                    >
                      <Mensaje
                        message={message}
                        nombresHerramientas={nombresHerramientas}
                        onConfirmar={confirmar}
                        onCancelar={cancelar}
                        onAnswer={responder}
                        onFileChoice={elegirArchivo}
                        onResolveProposal={resolveProposal}
                        resolvingProposal={resolvingProposal}
                        ocupado={isLoading}
                      />
                    </div>
                  ))}
                  <div className="mt-3">
                    <ReadingFile name={readingFile} />
                  </div>
                  <div ref={messagesEndRef} />
                </div>
              </div>
            </ScrollArea>

            <div className="border-t border-border px-4 py-3 md:px-6">
              <div className="mx-auto w-full max-w-3xl">
                {/* El clip sube el archivo y se lo dice al Brain (6.2); el
                    micrófono dicta a la caja sin enviar (brain-multimodal, D7). */}
                <ChatInput {...caja} />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
